import http, { request } from './http';

const BASE = '/ai-review';

export interface Result<T> {
  success: boolean;
  message?: string;
  data: T;
}

export interface DrawingUploadResult {
  fileName: string;
  storedPath: string;
  size: number;
}

export interface DrawingItem {
  objectKey: string;
  fileName: string;
  size: number;
  lastModified: string | null;
}

export interface DrawingItemWithFolder extends DrawingItem {
  folderPath: string;
  level1: string;
  level2: string | null;
}

export interface AccessUrlResult {
  token: string;
  accessUrl: string;
}

/** 上传图纸（PDF）；单文件上限见 GET /api/app/upload-limits 的 chat 场景 */
export async function uploadDrawing(
  file: File,
  folderPath?: string,
  onProgress?: (progress: number) => void
): Promise<DrawingUploadResult> {
  const form = new FormData();
  form.append('file', file);
  if (folderPath) form.append('folderPath', folderPath);
  const res = await request<Result<DrawingUploadResult>>(
    http.post(BASE + '/drawings/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
      onUploadProgress: (e: ProgressEvent) => {
        if (!onProgress) return;
        if (!e.total) return;
        const ratio = e.loaded / e.total;
        onProgress(Math.max(0, Math.min(1, ratio)));
      },
    })
  );
  const result = res as unknown as Result<DrawingUploadResult>;
  if (!result.success || !result.data) throw new Error(result.message ?? '上传失败');
  return result.data;
}

/** 图纸列表（按当前用户与可选目录） */
export async function listDrawings(folderPath?: string): Promise<DrawingItem[]> {
  const params: Record<string, string> = {};
  if (folderPath) params.folderPath = folderPath;
  const res = await request<Result<DrawingItem[]>>(
    http.get(BASE + '/drawings/list', { params })
  );
  const result = res as unknown as Result<DrawingItem[]>;
  if (!result.success) throw new Error(result.message ?? '列表失败');
  return result.data ?? [];
}

/** 全部图纸（单次递归列表，含虚拟目录路径） */
export async function listAllDrawings(): Promise<DrawingItemWithFolder[]> {
  const res = await request<Result<DrawingItemWithFolder[]>>(
    http.get(BASE + '/drawings/list-all'),
  );
  const result = res as unknown as Result<DrawingItemWithFolder[]>;
  if (!result.success) throw new Error(result.message ?? '列表失败');
  return result.data ?? [];
}

/** 获取图纸预览脱敏链接（Token URL） */
export async function getDrawingAccessUrl(objectKey: string): Promise<AccessUrlResult> {
  const res = await request<Result<AccessUrlResult>>(
    http.get(BASE + '/drawings/access-url', { params: { objectKey } })
  );
  const result = res as unknown as Result<AccessUrlResult>;
  if (!result.success) throw new Error(result.message ?? '获取预览链接失败');
  return result.data;
}

/** 删除图纸（当前用户目录下） */
export async function deleteDrawing(objectKey: string): Promise<void> {
  const res = await request<Result<null>>(
    http.delete(BASE + '/drawings', { params: { objectKey } })
  );
  const result = res as unknown as Result<null>;
  if (!result.success) throw new Error(result.message ?? '删除失败');
}

export interface DrawingMoveResult {
  objectKey: string;
  newObjectKey: string;
}

/** 批量移动图纸到目标目录；targetFolderPath 为空表示根目录 */
export async function moveDrawings(
  objectKeys: string[],
  targetFolderPath?: string | null,
): Promise<DrawingMoveResult[]> {
  const res = await request<Result<DrawingMoveResult[]>>(
    http.post(BASE + '/drawings/move', {
      objectKeys,
      targetFolderPath: targetFolderPath?.trim() || null,
    }),
  );
  const result = res as unknown as Result<DrawingMoveResult[]>;
  if (!result.success) throw new Error(result.message ?? '移动失败');
  return result.data ?? [];
}

// ---------- 智审（异步） ----------

export interface DesignReviewIssueLocation {
  page: number;
  /** [[x1,y1],[x2,y2]]：默认与 PDF.js scale=1 viewport 一致（左上 y 向下）；coordinateSystem 为 pdf 时为 PDF 用户空间两点 */
  coordinates?: number[][];
  /**
   * 与三方 visualize_review.py 一致：[x1,y1,x2,y2]，各分量先除以 bboxNormScale（默认 999）再乘页宽/页高。
   */
  bbox_2d?: number[];
  /** 归一化除数，默认 999；若三方用 1000 可传 1000 */
  bboxNormScale?: number;
  bbox_norm_scale?: number;
  /** 默认或不填：coordinates 已为 viewport 像素；pdf：PDF 用户空间；page_tl：PyMuPDF 页面左上原点 */
  coordinateSystem?: 'viewport' | 'pdf' | 'page_tl';
  coordinate_system?: 'viewport' | 'pdf' | 'page_tl';
  /** [dx,dy] 加在 viewport（scale=1）像素上，用于与三方栅格图原点对齐，例如更靠左/靠下时可试 [120,-20] */
  alignOffset?: number[];
}

export interface DesignReviewIssue {
  id: string;
  severity: string;
  description: string;
  location?: DesignReviewIssueLocation | null;
  rule: string;
  suggestion: string;
  /** 若三方把框放在根级，与 location.bbox_2d 二选一 */
  bbox_2d?: number[];
}

export interface DesignReviewThoughtStep {
  key?: string;
  icon?: string;
  title?: string;
  text?: string;
}

export interface DesignReviewResultData {
  status: string;
  progress?: number;
  message?: string;
  summary?: string;
  score?: number;
  issues?: DesignReviewIssue[];
  failedStep?: string;
  /** 面向用户的评审说明 */
  user_reply?: string;
  userReply?: string;
  structured_result?: Record<string, unknown>;
  structuredResult?: Record<string, unknown>;
  thought?: { steps?: DesignReviewThoughtStep[] };
}

export interface DesignReviewTaskIdBody {
  taskId: string;
}

/** 提交智审任务（后端 2D 解析 + 大模型评审，异步轮询结果） */
export async function submitDesignReviewTask(
  objectKey: string,
  documentType?: string,
  options?: string
): Promise<DesignReviewTaskIdBody> {
  const body = new URLSearchParams();
  body.set('objectKey', objectKey);
  if (documentType) body.set('documentType', documentType);
  if (options) body.set('options', options);
  const res = await request<Result<DesignReviewTaskIdBody>>(
    http.post(BASE + '/design/review/task', body, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      timeout: 120000,
    })
  );
  const result = res as unknown as Result<DesignReviewTaskIdBody>;
  if (!result.success || !result.data?.taskId) throw new Error(result.message ?? '提交智审失败');
  return result.data;
}

/** 轮询智审结果 */
export async function getDesignReviewResult(taskId: string): Promise<Result<DesignReviewResultData>> {
  const res = await request<Result<DesignReviewResultData>>(
    http.get(BASE + '/design/review/result', { params: { taskId }, timeout: 120000 })
  );
  return res as unknown as Result<DesignReviewResultData>;
}

/** 轮询直至 completed / failed（failed 时抛错） */
export async function pollDesignReviewResult(
  taskId: string,
  intervalMs = 1500,
  onProgress?: (data: DesignReviewResultData) => void,
): Promise<DesignReviewResultData> {
  for (;;) {
    const wrapped = await getDesignReviewResult(taskId);
    if (!wrapped.success || !wrapped.data) {
      throw new Error(wrapped.message ?? '轮询失败');
    }
    const d = wrapped.data;
    onProgress?.(d);
    if (d.status === 'failed') {
      throw new Error(d.message ?? '评审失败');
    }
    if (d.status === 'completed') {
      return d;
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
}

/** 将轮询中的 thought / progress 转为思考步骤 */
export function thoughtStepsFromReviewPoll(data: DesignReviewResultData): DesignReviewThoughtStep[] {
  const raw = data.thought?.steps;
  if (raw?.length) {
    return raw.map((s, idx) => ({
      key: String(s.key ?? idx),
      icon: String(s.icon ?? '⏳'),
      title: String(s.title ?? '步骤'),
      text: String(s.text ?? data.message ?? ''),
    }));
  }
  const p = data.progress ?? 0;
  const msg = data.message?.trim() || '处理中…';
  if (p < 45) {
    return [{ key: 'parse', icon: '⏳', title: '2D 图纸解析', text: msg }];
  }
  if (p < 100) {
    return [
      { key: 'parse', icon: '✅', title: '2D 图纸解析', text: '解析完成' },
      { key: 'llm', icon: '⏳', title: '大模型智审', text: msg },
    ];
  }
  return [];
}
